import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {DateField} from '@/components/date-field'
import {FieldError} from '@/components/field-error'
import {UploadTrigger} from '@/components/upload-trigger'
import {useEvidenceUploadFlow, useRegisterEvidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {subirEvidencia313} from '@/components/solicitudes-ips/subir-evidencia-313'
import type {AdjuntoArt16, Literal} from '@/hooks/salud/medicina_laboral/use-solicitudes-ips'

/** Res. 1843/2025 art. 16 b–e (KAI-154). El c (Matriz en Excel) solo cuando la Matriz no está en Kairos. */
export function AdjuntosArt16({
  businessId,
  adjuntos,
  conExcelDeMatriz,
  hoy,
  onChange,
}: {
  businessId: string
  adjuntos: AdjuntoArt16[]
  conExcelDeMatriz: boolean
  hoy: string
  onChange: (adjuntos: AdjuntoArt16[]) => void
}) {
  const {t} = useTranslation()
  const literales: Literal[] = conExcelDeMatriz ? ['b', 'c', 'd', 'e'] : ['b', 'd', 'e']
  return (
    <fieldset className="space-y-2" data-testid="solicitudIps-adjuntos">
      <legend className="text-xs uppercase tracking-wide text-muted-foreground">
        {t('solicitudIps.adjuntos.titulo')}
      </legend>
      {literales.map((literal) => (
        <FilaAdjunto
          key={literal}
          businessId={businessId}
          literal={literal}
          hoy={hoy}
          adjunto={adjuntos.find((a) => a.literal === literal) ?? null}
          onChange={(adjunto) =>
            onChange([...adjuntos.filter((a) => a.literal !== literal), ...(adjunto ? [adjunto] : [])])
          }
        />
      ))}
    </fieldset>
  )
}

function FilaAdjunto({
  businessId,
  literal,
  hoy,
  adjunto,
  onChange,
}: {
  businessId: string
  literal: Literal
  hoy: string
  adjunto: AdjuntoArt16 | null
  onChange: (adjunto: AdjuntoArt16 | null) => void
}) {
  const {t} = useTranslation()
  const registrar = useRegisterEvidence(businessId)
  const [fecha, setFecha] = useState(adjunto?.fecha ?? hoy)
  const upload = useEvidenceUploadFlow(async (file, onProgress) => {
    const nueva = await subirEvidencia313(businessId, registrar, file, onProgress)
    if (nueva) onChange({literal, evidence_id: nueva.id, filename: nueva.filename, fecha})
  })
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid={`solicitudIps-adjunto-${literal}`}>
      <span className="min-w-[14rem] text-sm">{t(`solicitudIps.adjuntos.literal.${literal}`)}</span>
      {adjunto ? (
        <>
          <span className="font-mono text-xs" data-testid={`solicitudIps-adjunto-nombre-${literal}`}>
            {adjunto.filename}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onChange(null)}
            data-testid={`solicitudIps-adjunto-quitar-${literal}`}>
            {t('solicitudIps.adjuntos.quitar')}
          </Button>
        </>
      ) : (
        <UploadTrigger
          label={t('solicitudIps.adjuntos.subir')}
          progress={upload.progress}
          testId={`solicitudIps-adjunto-subir-${literal}`}
          onPick={upload.start}
        />
      )}
      <DateField
        id={`solicitudIps-adjunto-fecha-${literal}`}
        label={t('solicitudIps.adjuntos.fecha')}
        value={fecha}
        max={hoy}
        onChange={(valor) => {
          setFecha(valor)
          if (adjunto) onChange({...adjunto, fecha: valor})
        }}
      />
      <FieldError error={upload.error ?? undefined} />
    </div>
  )
}
